import { AddRounded, VisibilityOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { fetchAdjustments } from "@/api/adjustments";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import AdjustmentDetailDialog from "@/components/finance/AdjustmentDetailDialog";
import NewAdjustmentDialog from "@/components/finance/NewAdjustmentDialog";
import StatusBadge, { type StatusTone } from "@/components/StatusBadge";
import { useAdjustmentRaisers, useAdjustments } from "@/hooks/useAdjustments";
import { useOrganizations } from "@/hooks/useOrganizations";
import { usePermissions } from "@/hooks/usePermissions";
import { type Adjustment, APPROVAL_STATUS_LABEL, ApprovalStatus } from "@/interfaces/adjustments";
import { formatAmount } from "@/utils/number-format";

const shortDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");

/**
 * Signed credits in the platform's format — +1,234.50. The sign is explicit so a removal can never
 * be misread as an add when it is one row among many.
 */
const credits = (a: Adjustment) => {
  const n = Number(a.amount);
  if (!Number.isFinite(n)) return "—";
  return `${n > 0 ? "+" : ""}${formatAmount(n)}`;
};

/** The queue state each approval status maps to, borrowed as a tone by the shared badge. */
const STATUS_TONE: Record<number, StatusTone> = {
  [ApprovalStatus.pending]: "warning",
  [ApprovalStatus.approved]: "success",
  [ApprovalStatus.rejected]: "error",
  // Withdrawn by its maker — over, and carrying no signal worth competing for attention.
  [ApprovalStatus.cancelled]: "muted",
};

const statusChip = (a: Adjustment) =>
  a.status == null ? (
    <StatusBadge label="—" tone="neutral" />
  ) : (
    <StatusBadge
      label={APPROVAL_STATUS_LABEL[a.status] ?? "—"}
      tone={STATUS_TONE[a.status] ?? "neutral"}
    />
  );

/** Quick status filter, the same shape the Invoices blotter uses. */
const STATUS_PILLS: { label: string; value: number | null }[] = [
  { label: "All", value: null },
  { label: "Pending", value: ApprovalStatus.pending },
  { label: "Approved", value: ApprovalStatus.approved },
  { label: "Rejected", value: ApprovalStatus.rejected },
  { label: "Cancelled", value: ApprovalStatus.cancelled },
];

function StatusPills({
  active,
  onChange,
}: {
  active: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {STATUS_PILLS.map((p) => {
        const isActive = active === p.value;
        return (
          <Button
            key={p.label}
            size="small"
            onClick={() => onChange(p.value)}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              border: "1px solid",
              borderColor: isActive ? "var(--color-rodeo-black)" : "var(--color-smoke)",
              bgcolor: isActive ? "var(--color-rodeo-black)" : "transparent",
              color: isActive ? "var(--color-white)" : "var(--color-graphite)",
              "&:hover": {
                bgcolor: isActive ? "var(--color-rodeo-black)" : "var(--color-cloud, #f2f0ec)",
              },
            }}
          >
            {p.label}
          </Button>
        );
      })}
    </Box>
  );
}

const oneOf = (filters: AppliedFilter[], id: string): string | undefined =>
  filters.find((f) => f.fieldId === id)?.values?.[0];

/**
 * Finance › Adjustments — every hand-raised credit change, and where each sits in the queue.
 *
 * GFG-only (adjust/CREDIT, scope all). A row here is a REQUEST, not a movement: only the ones
 * showing Approved have actually changed a balance, which is why the Applied column exists
 * separately from Status.
 */
export default function AdjustmentsPanel() {
  const { can } = usePermissions();
  const canRaise = can("adjust", "CREDIT");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [status, setStatus] = useState<number | null>(null);
  const [detail, setDetail] = useState<Adjustment | null>(null);

  // Filter OPTIONS. Organizations are bounded here rather than typeahead-searched: the Add-filter
  // menu wants a list, and the count of clients that have ever had an adjustment is small.
  const orgs = useOrganizations({ limit: 100 }).data?.data ?? [];
  const raisers = useAdjustmentRaisers().data ?? [];

  const filterFields: FilterField[] = [
    {
      id: "organization",
      label: "Organization",
      options: orgs.map((o) => ({ value: String(o.id), label: o.name ?? `#${o.id}` })),
    },
    {
      id: "raisedBy",
      label: "Raised by",
      options: raisers.map((r) => ({ value: String(r.id), label: r.name ?? `#${r.id}` })),
    },
    // The audit report's "for the period" — Will's 7.1.
    { id: "raised", label: "Raised", kind: "daterange", options: [] },
  ];

  const queryFrom = (filters: AppliedFilter[]) => {
    const [from = "", to = ""] = filters.find((f) => f.fieldId === "raised")?.values ?? [];
    const org = oneOf(filters, "organization");
    const by = oneOf(filters, "raisedBy");
    return {
      status: status ?? undefined,
      organizationId: org ? Number(org) : undefined,
      createdBy: by ? Number(by) : undefined,
      raisedFrom: from || undefined,
      raisedTo: to || undefined,
    };
  };

  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    page: number;
    limit: number;
  }): BlotterData<Adjustment> => {
    const { data, isLoading } = useAdjustments({
      search: q.search || undefined,
      ...queryFrom(q.filters),
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<Adjustment> = {
    searchPlaceholder: "Search organization, group, reason, note or who raised it",
    itemNoun: "adjustment",
    rowKey: (a) => a.id,
    defaultSortKey: "created",
    defaultSortDir: "desc",
    filterFields,
    useData,
    exportFilename: "adjustments.csv",
    headerAction: canRaise ? (
      <IconButton
        onClick={() => setDialogOpen(true)}
        title="New adjustment"
        aria-label="New adjustment"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <AddRounded />
      </IconButton>
    ) : undefined,
    exportFetch: async (q) => {
      const res = await fetchAdjustments({
        search: q.search || undefined,
        ...queryFrom(q.filters),
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    columns: [
      {
        key: "created",
        label: "Raised",
        sortable: true,
        sortValue: (a) => a.createdAt,
        render: (a) => shortDate(a.createdAt),
      },
      {
        // Org and group carry the same name on a single-wallet plan, which is most of them — two
        // columns for one fact. The group is shown only when it actually differs.
        key: "client",
        label: "Client",
        sortValue: (a) => a.organizationName ?? "",
        render: (a) => (
          <Box>
            <Box sx={{ fontWeight: 500 }}>{a.organizationName ?? "—"}</Box>
            {a.groupName && a.groupName !== a.organizationName ? (
              <Box sx={{ fontSize: 12, color: "var(--color-graphite)" }}>{a.groupName}</Box>
            ) : null}
          </Box>
        ),
        exportValue: (a) =>
          a.groupName && a.groupName !== a.organizationName
            ? `${a.organizationName ?? "—"} / ${a.groupName}`
            : (a.organizationName ?? "—"),
      },
      {
        key: "reason",
        label: "Reason",
        sortValue: (a) => a.reasonLabel ?? a.reasonCode ?? "",
        render: (a) => a.reasonLabel ?? a.reasonCode ?? "—",
      },
      {
        key: "credits",
        label: "Credits",
        sortable: true,
        sortValue: (a) => Number(a.amount ?? 0),
        render: (a) => (
          <Box sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{credits(a)}</Box>
        ),
        exportValue: credits,
      },
      {
        key: "status",
        label: "Status",
        sortValue: (a) => a.status ?? -1,
        render: statusChip,
        exportValue: (a) => (a.status == null ? "—" : (APPROVAL_STATUS_LABEL[a.status] ?? "—")),
      },
      {
        key: "applied",
        label: "Applied",
        sortable: true,
        sortValue: (a) => a.appliedAt ?? "",
        render: (a) => shortDate(a.appliedAt),
      },
      {
        key: "raisedBy",
        label: "Raised by",
        sortValue: (a) => a.createdByName ?? "",
        render: (a) => a.createdByName ?? "—",
      },
      // The note is NOT a column: it is the maker's justification, and a truncated one is worse
      // than none. It reads in full in the detail dialog, with the decisions taken on it.
    ],
    // Opening the audit view — the same affordance the Refunds blotter uses.
    leadingCell: (a) => (
      <Tooltip title="View adjustment" arrow>
        <IconButton size="small" aria-label="View adjustment" onClick={() => setDetail(a)}>
          <VisibilityOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
  };

  return (
    <>
      <StatusPills active={status} onChange={setStatus} />
      <Blotter config={config} />
      <NewAdjustmentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      <AdjustmentDetailDialog adjustment={detail} onClose={() => setDetail(null)} />
    </>
  );
}
